import * as React from 'react';

import {TimelineBannerContent} from 'shells/timeline/banner/timeline-banner-content';
import {TIMELINE_ENTRY_ICONS} from 'shells/timeline/gutter/consts';
import {TimelineEntryIcon} from 'shells/timeline/gutter/timeline-entry-icon';
import type {TimelineEntryIconVariant} from 'shells/timeline/gutter/types';
import {AddReactionButton} from 'shells/timeline/reaction-bar/add-reaction-button';

import type {
    EventFragmentSparse_actor as Actor,
    ReactionFragment as Reaction,
    ReactionType,
    SessionUser,
} from 'nutshell-graphql-types';

import {getFilteredReactions, getToggleAction} from '../reactions/helpers';
import {useToggleReaction} from '../reactions/reaction-mutations';
import {TimelineReactionBar} from '../reactions/timeline-reaction-bar';
import {TimelineEntryContext} from '../timeline';

import styles from './timeline-banner.module.css';

type ReactableBanner = {
    currentUser: SessionUser;
    reactions: Reaction[];
};

type Props = {
    id: string;
    iconVariant: TimelineEntryIconVariant;
    timestamp: number;
    actor: Actor | null | undefined;
    action: React.ReactElement<any>;
    reactableBanner?: ReactableBanner;
    bannerBody?: React.ReactElement<any>;
    omitActor?: boolean;
};

export function TimelineBanner(props: Props) {
    const {id, iconVariant, timestamp, actor, action, reactableBanner, bannerBody, omitActor} =
        props;

    const timelineBannerRef = React.useRef();

    let reactions, currentUser;
    if (reactableBanner) {
        reactions = reactableBanner.reactions;
        currentUser = reactableBanner.currentUser;
    }

    const toggleReaction = useToggleReaction();

    // Get filtered reactions for reactions list
    const filteredReactions = reactions ? getFilteredReactions(reactions) : undefined;
    const reactionToggleAction =
        filteredReactions && currentUser
            ? // @ts-expect-error
              (reaction) => getToggleAction(reaction, filteredReactions, currentUser.id)
            : undefined;

    const onToggleReaction =
        reactionToggleAction && currentUser
            ? (reactionType: ReactionType) => {
                  toggleReaction({
                      changeLogId: id,
                      reactionType,
                      user: currentUser,
                      toggleAction: reactionToggleAction(reactionType),
                  });
              }
            : undefined;

    const isLastEntry = Boolean(React.useContext(TimelineEntryContext).lastEntryId === id);
    const isTimelineSmall = Boolean(React.useContext(TimelineEntryContext).isTimelineSmall);

    return (
        <div
            ref={(ref) => {
                // @ts-expect-error
                timelineBannerRef.current = ref;
            }}
            className={
                isLastEntry ? styles['banner-container--last-entry'] : styles['banner-container']
            }
        >
            <div className='flex full-width'>
                <TimelineEntryIcon
                    // @ts-expect-error
                    icon={TIMELINE_ENTRY_ICONS[iconVariant]}
                    gutterContainerRef={timelineBannerRef}
                    isSmall={isTimelineSmall}
                    isLastEntry={isLastEntry}
                />
                <div className={styles.content}>
                    <TimelineBannerContent
                        timestamp={timestamp}
                        actor={actor}
                        action={action}
                        isSmall={isTimelineSmall}
                        omitActor={omitActor}
                    />
                    {filteredReactions && onToggleReaction && currentUser ? (
                        <>
                            {Object.keys(filteredReactions).length ? (
                                <div className={styles['reaction-bar-container']}>
                                    <TimelineReactionBar
                                        reactions={filteredReactions}
                                        currentUserId={currentUser.id}
                                        onToggleReaction={onToggleReaction}
                                    />
                                </div>
                            ) : (
                                <div className={styles['reaction-button-container']}>
                                    <AddReactionButton onSelectReaction={onToggleReaction} />
                                </div>
                            )}
                        </>
                    ) : undefined}
                </div>
            </div>
            {bannerBody ? (
                <div
                    className={
                        isTimelineSmall ? styles['banner-body--small'] : styles['banner-body']
                    }
                >
                    {bannerBody}
                </div>
            ) : undefined}
        </div>
    );
}
